import { useEffect, useRef, useState } from 'react';

/**
 * Animates a number from 0 (or previous value) to the target.
 * Uses requestAnimationFrame with ease-out for a smooth count-up effect.
 */
export default function useCountUp(
  target: number,
  duration = 600,
  enabled = true,
): number {
  const [value, setValue] = useState(0);
  const prevTarget = useRef(0);
  const rafId = useRef(0);

  useEffect(() => {
    if (!enabled || duration <= 0) { setValue(target); prevTarget.current = target; return; }

    const from = prevTarget.current;
    const to = target;
    prevTarget.current = to;

    if (from === to) { setValue(to); return; }

    const start = performance.now();
    const diff = to - from;

    function tick(now: number) {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      // Ease-out cubic — decelerating
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + diff * eased));
      if (t < 1) rafId.current = requestAnimationFrame(tick);
    }

    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId.current);
  }, [target, duration, enabled]);

  return value;
}
